import {Component, computed, inject, linkedSignal, signal} from '@angular/core';
import {TranslocoDirective, TranslocoService} from "@jsverse/transloco";
import {AuthenticationService} from "@services";
import {ButtonComponent, InputComponent, InputContainerComponent} from "@components";
import {DatePipe, NgOptimizedImage} from "@angular/common";
import {FormControl, FormGroup, FormsModule, ReactiveFormsModule, Validators} from "@angular/forms";
import {ToastService} from "@components";
import {CreateMediaRequestDto, MediaDto} from "@api-contracts";
import {MediaService} from "@api-services";
import {HttpContext} from "@angular/common/http";
import {SHOW_SPINNER} from "../../../../app.config";
import {TagInputComponent} from "../../../../components/forms/tag-input/tag-input.component";

@Component({
  selector: 'wun-overview',
  imports: [
    TranslocoDirective,
    ButtonComponent,
    NgOptimizedImage,
    DatePipe,
    FormsModule,
    InputComponent,
    InputContainerComponent,
    ReactiveFormsModule,
    TagInputComponent
  ],
  templateUrl: './overview.component.html',
  styleUrl: './overview.component.scss'
})
export class OverviewComponent {

  public readonly authService = inject(AuthenticationService);
  private readonly toastService = inject(ToastService);
  private readonly t = inject(TranslocoService)
  private readonly mediaService = inject(MediaService);

  public readonly editing = signal<boolean>(false)
  public readonly currentMedia = signal<CreateMediaRequestDto | undefined>(undefined)
  private readonly file = signal<File | undefined>(undefined)

  isArtist = computed(() => this.authService.current()?.profileType === 'ARTIST');

  updateUserForm = computed(() => {
    const user = this.authService.current();
    return new FormGroup({
      name: new FormControl(user?.firstName, Validators.required),
      surname: new FormControl(user?.lastName, Validators.required),
      nickname: new FormControl(user?.nickname ?? ''),
      useNickname: new FormControl(user?.displayNamePreference === 'NICKNAME'),
      country: new FormControl(user?.country, Validators.required),
      address: new FormControl(user?.defaultAddress?.street, Validators.required),
      phone: new FormControl(user?.phone, Validators.required),
      birthDate: new FormControl(user?.birthday, Validators.required),
      description: new FormControl(user?.description?.toString(), Validators.required),
      city: new FormControl(user?.city, Validators.required),
      coverId: new FormControl<number | undefined>(undefined),
      galleryCode: new FormControl<string>(user?.assignedToGallery ?? '')
    });
  });

  tags = linkedSignal(() => {
    const tags = this.authService.current();
    try {
      return JSON.parse(tags?.topics || '[]');
    } catch (ex) {
      return [];
    }
  })

  updateMe() {
    const v = this.updateUserForm().value;
    this.authService.updateMe(
      {
        updateMeRequestDto: {
          birthday: v.birthDate!,
          country: v.country!,
          phone: v.phone!,
          city: v.city!,
          description: v.description!,
          firstName: v.name!,
          lastName: v.surname!,
          interests: this.tags(),
          coverId: v.coverId,
          nickname: v.nickname,
          displayNamePreference: v.useNickname ? 'NICKNAME' : 'FULL_NAME',
          ...(this.isArtist() ? {assignedToGallery: (v.galleryCode || '').trim() || null} : {})
        } as any
      }
    ).subscribe({
      error: (err) => {
        if (err?.error?.message === 'INVALID_GALLERY_CODE' || err?.error?.detail === 'INVALID_GALLERY_CODE')
          this.toastService.addMessage({
            id: '',
            title: this.t.translate('profile.galleryCodeInvalid.title'),
            content: this.t.translate('profile.galleryCodeInvalid.description'),
            severity: 'error',
          })
      },
      next: () => {
        const current = this.authService.current();
        if (current && this.isArtist())
          (current as any).assignedToGallery = (v.galleryCode || '').trim() || null;
        this.editing.set(false)
        this.toastService.addMessage({
          id: '',
          title: this.t.translate('profile.userUpdated.title'),
          content: this.t.translate('profile.userUpdated.description'),
          severity: 'success',
        })
      }
    })
  }

  fileChanged($event: HTMLInputElement) {
    const file = new FileReader()
    const _file = $event.files![0];
    this.file.set(_file);
    file.onloadend = (e: Event) => {
      const dataUrl = file.result as string;
      const img = new Image();
      img.onload = () => {
        this.currentMedia.set({
          height: img.height,
          width: img.width,
          name: _file.name,
          mimetype: _file.type,
          fileScaling: 'DEFAULT',
          size: _file.size,
          extension: _file.name.split('.').pop() || '',
          url: dataUrl,
          user: this.authService.current()!.id
        })
      }
      img.src = dataUrl;
    }
    file.readAsDataURL($event.files![0])
  }

  setProfileImage() {
    const media = this.currentMedia()!;
    this.mediaService.mediaUpload({
      file: this.file() as Blob,
      ...media,
      url: ''
    }, "body", false, {
      context: new HttpContext().set(SHOW_SPINNER, true)
    }).subscribe(d => {
      this.updateUserForm().patchValue({
        coverId: d.data.id!
      })
      this.updateMe()
    })
  }
}
